<template>
    <v-container fluid class="pa-0">
        <v-row v-if="userMeasurementSet === null">
            <v-col>
                <!-- Data Collection Tasks -->
                <task-measurements-list
                    v-if="taskSchedule != null"
                    :task-schedule="taskSchedule"
                    :protocol="protocol"
                    :experiment="experiment"
                    :showV1OTDataButton="true"
                    @showSummaryMeasurementsForTask="showSummaryMeasurementsForTask"
                    @showUserMeasurementsForTask="showUserMeasurementsForTask"
                    @showQCDetailDialog="showQCDetailDialog"
                    @showV1OTData="showV1OTData"
                    @downloadDataTemplate="downloadDataTemplate"
                    @saved="fetchTaskSchedule"
                    @deleted="fetchTaskSchedule"
                    @showImportUserMeasurementsDialog="showImportUserMeasurementsDialog"
                    @reloadData="fetchTaskSchedule"
                />

                <v-dialog v-model="isUserMeasurementSetListDialogOpen">
                    <!-- This opens a dialog so we avoid having to scroll anywhere to see the list -->
                    <user-measurement-sets-list
                        v-if="isUserMeasurementSetListDialogOpen"
                        :protocol="protocol"
                        :taskSchedule="taskSchedule"
                        :dataCollectionTask="selectedDataCollectionTask"
                        :taskGroup="taskGroupForSelectedDataCollectionTask"
                        :experiment="experiment"
                        @viewUserMeasurementSet="viewUserMeasurementSet"
                        @close="closeUserMeasurementSetListDialog"
                    />
                </v-dialog>

                <v-dialog v-model="isSummaryPerTaskMeasurementsListDialogOpen">
                    <summary-per-task-measurements-list
                        v-if="showMeasurements === 'summary'"
                        ref="summaryPerTaskMeasurementsList"
                        :experiment="experiment"
                        :protocol="protocol"
                        :taskSchedule="taskSchedule"
                        :dataCollectionTask="selectedDataCollectionTask"
                        :taskGroup="taskGroupForSelectedDataCollectionTask"
                        @close="closeSummaryMeasurementsListDialog"
                    />
                </v-dialog>

                <v-dialog v-model="isV1OTDataDialogOpen">
                    <legacy-thing-instance-ot-data
                        :protocol="protocol"
                        :experiment="experiment"
                        @close="isV1OTDataDialogOpen = false"
                    />
                </v-dialog>

                <DataQCDetailDialog
                    v-if="isQCDetailDialogOpen"
                    v-model="isQCDetailDialogOpen"
                    :qcItem="selectedQCItem"
                    @save="closeQCDetailDialog"
                    @cancel="cancelQCDetailDialog"
                    :schemaConfig="protocol"
                    :taskSchedule="taskSchedule"
                    :dataCollectionTask="selectedDataCollectionTask"
                    :experiment="experiment"
                />

                <v-dialog v-model="isUserMeasurementsImportDialogOpen" max-width="75%" persistent>
                    <import-user-measurements-form
                        :experiment="experiment"
                        :protocol="protocol"
                        :dataCollectionTask="null"
                        :collect-user-date="false"
                        :readout-group="null"
                        :user-id="user.email"
                        :task-display-name="null"
                        @saved="savedImportedUserMeasurements"
                        @error="displayImportError"
                        @closed="closeImportUserMeasurementsDialog"
                    />
                </v-dialog>
            </v-col>
        </v-row>
        <v-row v-else>
            <v-col>
                <v-card>
                    <v-card-title>
                        <v-btn @click="viewTaskList">Back to Tasks</v-btn>
                    </v-card-title>
                </v-card>
                <user-measurements-table-form
                    :task-schedule="taskSchedule"
                    :user-measurement-set="userMeasurementSet"
                    :users-with-measurement-sets="usersWithMeasurementSets"
                    :readout-group="readoutGroup"
                    :data-collection-task="dataCollectionTask"
                    :experiment="experiment"
                    :protocol="protocol"
                    @closed="viewTaskList"
                />
            </v-col>
        </v-row>
    </v-container>
</template>
<script>
import { defineComponent } from "vue";

import UserMeasurementSetsList from "../Measurements/UserMeasurementSetsList.vue";
import { withLoadingScreen } from "@/lib/loading";
import { measurementsAPI } from "@/services/api.services";
import { buildTaskScheduleHelper } from "@/lib/taskUtils";
import SummaryPerTaskMeasurementsList from "../Measurements/SummaryPerTaskMeasurementsList.vue";
import TaskMeasurementsList from "../Measurements/TaskMeasurementsList.vue";
import UserMeasurementsTableForm from "../Measurements/UserMeasurementsTableForm.vue";
import LegacyThingInstanceOtData from "./LegacyThingInstanceOTData.vue";
import DataQCDetailDialog from "../DataQC/DataQCDetailDialog.vue";
import { mapGetters } from "vuex";
import ImportUserMeasurementsForm from "../Measurements/ImportUserMeasurementsForm.vue";
import { buildMeasurementsImportMessage } from "@/lib/measurementsUtils";
import { downloadFile } from "@/lib/downloadFileUtil.js";
import { useToast } from "vue-toastification";

export default defineComponent({
    name: "user-measurements-tab",

    components: {
        LegacyThingInstanceOtData,
        UserMeasurementsTableForm,
        TaskMeasurementsList,
        SummaryPerTaskMeasurementsList,
        UserMeasurementSetsList,
        DataQCDetailDialog,
        ImportUserMeasurementsForm,
    },

    props: {
        protocol: {
            type: Object, // AbstractSchemaConfig
            required: true,
        },
        experiment: {
            type: Object,
            required: true,
        },
    },

    setup() {
        return {
            toast: useToast(),
        };
    },

    data() {
        return {
            taskSchedule: null,
            taskScheduleHelper: null,
            selectedDataCollectionTask: null,
            taskGroupForSelectedDataCollectionTask: null,
            showMeasurements: null,
            isUserMeasurementSetListDialogOpen: false,
            isSummaryPerTaskMeasurementsListDialogOpen: false,
            isV1OTDataDialogOpen: false,
            isQCDetailDialogOpen: false,
            selectedQCItem: null,
            // selected user measurement set objects
            userMeasurementSet: null,
            dataCollectionTask: null,
            taskGroup: null,
            readoutGroup: null,
            thingSchema: null,
            usersWithMeasurementSets: null,
            isUserMeasurementsImportDialogOpen: false,
        };
    },

    mounted() {
        this.fetchTaskSchedule();
    },

    computed: {
        ...mapGetters({
            user: "getAuthUser",
        }),
    },

    methods: {
        async fetchTaskSchedule() {
            withLoadingScreen(async () => {
                const taskSchedule =
                    await measurementsAPI.fetchTaskScheduleWithCollectionInfoForExperimentCode(
                        this.experiment.code
                    );
                this.renderTaskSchedule(taskSchedule);
            });
        },
        renderTaskSchedule(taskSchedule) {
            this.taskSchedule = taskSchedule;
            this.taskScheduleHelper = buildTaskScheduleHelper(taskSchedule);
        },
        showUserMeasurementsForTask(dataCollectionTask) {
            this.setSelectedTaskAndReadoutGroup(dataCollectionTask);
            this.showMeasurements = "user";
            this.isUserMeasurementSetListDialogOpen = true;
        },
        closeUserMeasurementSetListDialog() {
            this.isUserMeasurementSetListDialogOpen = false;
        },
        showSummaryMeasurementsForTask(dataCollectionTask) {
            this.setSelectedTaskAndReadoutGroup(dataCollectionTask);
            this.showMeasurements = "summary";
            this.isSummaryPerTaskMeasurementsListDialogOpen = true;
        },
        closeSummaryMeasurementsListDialog() {
            this.isUserMeasurementSetListDialogOpen = false;
        },
        showV1OTData() {
            this.isV1OTDataDialogOpen = true;
        },
        async downloadDataTemplate(selectedItems) {
            await withLoadingScreen(async () => {
                const taskIds = selectedItems;
                const userId = this.user.email;
                const response = await measurementsAPI.downloadMultiTaskUserMeasurementsTemplate(
                    taskIds,
                    userId
                );
                const filename = `User Measurements ${this.experiment.code}.xlsx`;
                downloadFile(filename, response);
            });
        },
        showQCDetailDialog(qcItem, dataCollectionTask) {
            this.selectedQCItem = { ...qcItem, investigator_comments: qcItem.investigatorComments };
            this.selectedDataCollectionTask = dataCollectionTask;
            this.isQCDetailDialogOpen = true;
        },
        closeQCDetailDialog() {
            this.isQCDetailDialogOpen = false;
            this.fetchTaskSchedule();
        },
        cancelQCDetailDialog() {
            this.isQCDetailDialogOpen = false;
        },
        showImportUserMeasurementsDialog() {
            this.isUserMeasurementsImportDialogOpen = true;
        },
        closeImportUserMeasurementsDialog() {
            this.isUserMeasurementsImportDialogOpen = false;
        },
        savedImportedUserMeasurements(userMeasurementImportResult) {
            this.isUserMeasurementsImportDialogOpen = false;
            // display toast with success/skipped count message
            const importMeasurementSetResults =
                userMeasurementImportResult.importMeasurementSetResults;
            const skippedRows = importMeasurementSetResults.flatMap((result) => result.skippedRows);
            const skippedAttributes = importMeasurementSetResults.flatMap(
                (result) => result.skippedAttributes
            );
            const skippedSheets = userMeasurementImportResult.skippedSheets;
            const message = buildMeasurementsImportMessage({
                skippedRows,
                skippedAttributes,
                skippedSheets,
            });
            this.toast.success(message);
        },
        displayImportError(error) {
            this.toast.error(error);
            this.showImportUserMeasurementsForm = false;
        },
        setSelectedTaskAndReadoutGroup(dataCollectionTask) {
            this.selectedDataCollectionTask = dataCollectionTask;
            this.taskGroupForSelectedDataCollectionTask = this.taskScheduleHelper.getTaskGroupById(
                dataCollectionTask.taskGroupId
            );
        },
        viewUserMeasurementSet({
            userMeasurementSet,
            dataCollectionTask,
            taskGroup,
            readoutGroup,
            thingSchema,
            usersWithMeasurementSets,
        }) {
            this.userMeasurementSet = userMeasurementSet;
            this.dataCollectionTask = dataCollectionTask;
            this.taskGroup = taskGroup;
            this.readoutGroup = readoutGroup;
            this.thingSchema = thingSchema;
            this.usersWithMeasurementSets = usersWithMeasurementSets;
        },
        async viewTaskList() {
            this.userMeasurementSet = null;
            this.dataCollectionTask = null;
            this.taskGroup = null;
            this.readoutGroup = null;
            this.thingSchema = null;
            // Note: Danielle wants to return to the task list, not to the user measurement set list
            this.isUserMeasurementSetListDialogOpen = false;
            await this.fetchTaskSchedule();
        },
    },
});
</script>
<style></style>
